import { Link, createRoute } from '@tanstack/react-router';
import { useBillingStatus } from '../api/billing';
import { HelperError } from '../api/helper';
import { useDeleteTranscript, useTranscripts } from '../api/helper-queries';
import { useSyncedTranscripts } from '../api/sync';
import { useHelperStore } from '../stores/helper';
import { rootRoute } from './__root';

export const transcriptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transcripts',
  component: TranscriptsPage,
});

function TranscriptsPage() {
  const credentials = useHelperStore((s) => s.credentials);
  const transcripts = useTranscripts();
  const remove = useDeleteTranscript();
  const billing = useBillingStatus();
  const subActive = (billing.data?.sub_tier ?? 'free') !== 'free';
  // Only call sync when the subscription is active; otherwise the
  // backend returns 403 and React Query would mark it as error state.
  const synced = useSyncedTranscripts({ enabled: subActive });

  if (!credentials) {
    return (
      <section>
        <h1 style={{ marginTop: 0 }}>Transcripts</h1>
        <p>
          Pair the helper first. <Link to="/pair">Go to pairing →</Link>
        </p>
      </section>
    );
  }

  // Merge local + sync sets keyed by transcript_id. Local rows win;
  // sync-only rows are surfaced with a Cloud badge.
  const localById = new Map(
    (transcripts.data?.transcripts ?? []).map((t) => [t.id, t]),
  );
  const syncOnly = (synced.data?.transcripts ?? []).filter(
    (s) => !localById.has(s.transcript_id),
  );

  return (
    <section style={{ maxWidth: 960 }}>
      <h1 style={{ marginTop: 0 }}>Transcripts</h1>
      <p style={{ color: '#666' }}>
        Meeting transcripts captured by the extension and persisted on this machine. Open a row to
        view the full text, summary, and downloads.
      </p>

      {!subActive && (
        <div
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 8,
            padding: '12px 14px',
            margin: '8px 0 20px',
            fontSize: 14,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <strong>Sync your transcripts across devices</strong> — included with your
            $3/mo subscription. Encrypted on this device; the server never sees plaintext.
          </span>
          <Link
            to="/pricing"
            style={{
              padding: '6px 12px',
              background: '#2563eb',
              color: 'white',
              textDecoration: 'none',
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            Subscribe
          </Link>
        </div>
      )}

      {transcripts.isLoading && <p>Loading transcripts…</p>}
      {transcripts.error && <p style={{ color: 'crimson' }}>{describeError(transcripts.error)}</p>}
      {transcripts.data && transcripts.data.transcripts.length === 0 && (
        <p style={{ color: '#666' }}>
          No transcripts yet. Start a meeting with the AI Assist extension to capture one.
        </p>
      )}
      {transcripts.data && transcripts.data.transcripts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e5e5' }}>
              <th style={{ padding: '8px 4px' }}>Source</th>
              <th style={{ padding: '8px 4px' }}>Started</th>
              <th style={{ padding: '8px 4px' }}>Duration</th>
              <th style={{ padding: '8px 4px' }}>Lines</th>
              <th style={{ padding: '8px 4px' }}>Summary</th>
              <th style={{ padding: '8px 4px' }} />
            </tr>
          </thead>
          <tbody>
            {transcripts.data.transcripts.map((t) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 4px', maxWidth: 320, overflow: 'hidden' }}>
                  <Link to="/transcripts/$id" params={{ id: t.id }}>
                    {hostnameOf(t.source_url) ?? t.source_url}
                  </Link>
                </td>
                <td style={{ padding: '8px 4px' }}>{new Date(t.started_at_ms).toLocaleString()}</td>
                <td style={{ padding: '8px 4px' }}>
                  {formatDuration(t.ended_at_ms - t.started_at_ms)}
                </td>
                <td style={{ padding: '8px 4px' }}>{t.line_count}</td>
                <td style={{ padding: '8px 4px' }}>{t.has_summary ? '✓' : '—'}</td>
                <td style={{ padding: '8px 4px', display: 'flex', gap: 4 }}>
                  <Link
                    to="/transcripts/$id"
                    params={{ id: t.id }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      textDecoration: 'none',
                    }}
                  >
                    Open
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Delete this transcript? This cannot be undone.')) {
                        remove.mutate(t.id);
                      }
                    }}
                    disabled={remove.isPending}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {subActive && syncOnly.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 16, margin: '0 0 4px 0' }}>From other devices</h2>
          <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
            Captured on another device + synced to the cloud, encrypted. Open them on the device
            that captured them to view the full transcript.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e5e5' }}>
                <th style={{ padding: '8px 4px' }}>Transcript ID</th>
                <th style={{ padding: '8px 4px' }}>Last updated</th>
                <th style={{ padding: '8px 4px' }}>Size</th>
              </tr>
            </thead>
            <tbody>
              {syncOnly.map((s) => (
                <tr key={s.transcript_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 4px', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {s.transcript_id.slice(0, 8)}…
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    {new Date(s.updated_at_ms).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    {(s.ciphertext_size / 1024).toFixed(1)} KiB
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </section>
  );
}

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function describeError(e: unknown): string {
  if (e instanceof HelperError) {
    return `${e.problem?.title ?? 'helper error'} (${e.status})${e.problem?.detail ? `: ${e.problem.detail}` : ''}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
